import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Rss, Plus, Play, Pause, Settings, Clock, CheckCircle, XCircle, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { useToastContext } from '../contexts/ToastContext'
import AddFeedModal from '../components/AddFeedModal'
import EditFeedModal from '../components/EditFeedModal'
import DeleteFeedDialog from '../components/DeleteFeedDialog'

export default function FeedsPage() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingFeed, setEditingFeed] = useState<any>(null)
  const [deletingFeed, setDeletingFeed] = useState<any>(null)
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useToastContext()

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.getCollections(),
  })

  const { data: feeds, isLoading: feedsLoading } = useQuery({
    queryKey: ['feeds'],
    queryFn: () => api.getFeeds(),
  })

  const triggerPollMutation = useMutation({
    mutationFn: ({ feedId, feedName }: { feedId: string; feedName: string }) => 
      api.triggerFeedPoll(feedId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feeds'] })
      showSuccess(
        'Feed Poll Triggered',
        `Successfully queued polling for "${variables.feedName}". The feed will be polled shortly.`,
        6000
      )
    },
    onError: (error: Error, variables) => {
      console.error('Failed to trigger feed poll:', error)
      showError(
        'Poll Failed',
        `Failed to trigger polling for "${variables.feedName}": ${error.message}`,
        8000
      )
    }
  })

  const deleteFeedMutation = useMutation({
    mutationFn: ({ feedId, feedName, deleteObjects }: { feedId: string; feedName: string; deleteObjects: boolean }) => 
      api.deleteFeed(feedId, deleteObjects),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feeds'] })
      setDeletingFeed(null)
      
      const objectsMessage = variables.deleteObjects && data.deletedObjectsCount 
        ? ` and ${data.deletedObjectsCount} objects`
        : '';
      
      showSuccess(
        'Feed Deleted',
        `Successfully deleted feed "${variables.feedName}"${objectsMessage}.`,
        5000
      )
    },
    onError: (error: Error, variables) => {
      console.error('Failed to delete feed:', error)
      showError(
        'Delete Failed',
        `Failed to delete feed "${variables.feedName}": ${error.message}`,
        6000
      )
    }
  })

  const handleAddFeed = () => {
    setIsAddModalOpen(true)
  }

  const handleTriggerPoll = async (feedId: string, feedName: string) => {
    if (window.confirm(`Manually trigger polling for "${feedName}"?`)) {
      try {
        console.log('Triggering poll for feed:', feedId)
        triggerPollMutation.mutate({ feedId, feedName })
      } catch (error) {
        console.error('Error in handleTriggerPoll:', error)
        showError('Unexpected Error', 'An unexpected error occurred while triggering the poll.')
      }
    }
  }

  const handleEditFeed = (feed: any) => {
    setEditingFeed(feed)
  }

  const handleDeleteFeed = (feedId: string, feedName: string) => {
    setDeletingFeed({ id: feedId, name: feedName })
  }

  const confirmDeleteFeed = (deleteObjects: boolean) => {
    if (deletingFeed) {
      try {
        console.log('Deleting feed:', deletingFeed.id, 'deleteObjects:', deleteObjects)
        deleteFeedMutation.mutate({ 
          feedId: deletingFeed.id, 
          feedName: deletingFeed.name, 
          deleteObjects 
        })
      } catch (error) {
        console.error('Error in confirmDeleteFeed:', error)
        showError('Unexpected Error', 'An unexpected error occurred while deleting the feed.')
        setDeletingFeed(null)
      }
    }
  }

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Threat Feeds</h1>
          <p className="text-gray-600">
            Manage external threat intelligence feed subscriptions
          </p>
        </div>
        <button 
          onClick={handleAddFeed}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Feed</span>
        </button>
      </div>

      {feeds?.feeds && feeds.feeds.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {feeds.feeds.map((feed: any) => (
              <li key={feed.id}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 rounded-md p-2">
                      <Rss className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {feed.name}
                      </h3>
                      <p className="text-sm text-gray-500">{feed.taxii_url}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          feed.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {feed.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Poll: every {feed.poll_interval_minutes}m
                        </span>
                        {feed.last_poll_time && (
                          <span className="text-xs text-gray-500">
                            Last: {new Date(feed.last_poll_time).toLocaleString()}
                          </span>
                        )}
                        {feed.last_poll_stats && (
                          <span className="text-xs">
                            Objects: <span className="text-green-600 font-medium">+{feed.last_poll_stats.objects_new}</span> <span className="text-yellow-600 font-medium">~{feed.last_poll_stats.objects_updated}</span> <span className="text-gray-500">={feed.last_poll_stats.objects_unchanged}</span>
                          </span>
                        )}
                        {/* Collection Information */}
                        {feed.collection_id && collections && (
                          <div className="flex items-center mt-2">
                            <span className="text-xs font-medium text-gray-700 mr-2">Collection:</span>
                            {(() => {
                              const collection = collections.collections?.find(c => c.id === feed.collection_id)
                              return collection ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                                  📁 {collection.title}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700">
                                  🔗 {feed.collection_id}
                                </span>
                              )
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => handleTriggerPoll(feed.id, feed.name)}
                      disabled={triggerPollMutation.isPending}
                      className="text-gray-400 hover:text-green-600 disabled:opacity-50"
                      title="Trigger manual poll"
                    >
                      <Play className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => handleEditFeed(feed)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit feed settings"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteFeed(feed.id, feed.name)}
                      disabled={deleteFeedMutation.isPending}
                      className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title="Delete feed"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Rss className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No feeds configured</h3>
          <p className="text-gray-500 mb-6">
            Add external TAXII feeds to automatically ingest threat intelligence data.
          </p>
          <button 
            onClick={handleAddFeed}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Add Your First Feed
          </button>
        </div>
      )}

      {/* Collection-Feed Relationships */}
      {collections?.collections && collections.collections.length > 0 && feeds?.feeds && feeds.feeds.length > 0 && (
        <div className="mt-8 bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Collection Sources</h2>
          <p className="text-sm text-gray-600 mb-4">
            Shows which threat feeds are configured for each collection
          </p>
          <div className="space-y-4">
            {collections.collections.map((collection: any) => {
              const associatedFeeds = feeds.feeds.filter((feed: any) => feed.collection_id === collection.id)
              return (
                <div key={collection.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">📁 {collection.title}</h3>
                      <p className="text-xs text-gray-500">{collection.id}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-gray-600">
                        {associatedFeeds.length} feed{associatedFeeds.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  {associatedFeeds.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {associatedFeeds.map((feed: any) => (
                        <div key={feed.id} className="flex items-center justify-between bg-gray-50 rounded-md p-2">
                          <div className="flex items-center space-x-2">
                            <div className={`h-2 w-2 rounded-full ${feed.is_active ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                            <span className="text-sm text-gray-900">{feed.name}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span>Every {feed.poll_interval_minutes}m</span>
                            {feed.last_poll_time && (
                              <span>
                                Last: {new Date(feed.last_poll_time).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 text-center py-4 text-gray-500">
                      <span className="text-sm">No feeds configured for this collection</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <AddFeedModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        collections={collections?.collections || []}
      />

      {editingFeed && (
        <EditFeedModal
          isOpen={!!editingFeed}
          onClose={() => setEditingFeed(null)}
          feed={editingFeed}
          collections={collections?.collections || []}
        />
      )}

      <DeleteFeedDialog
        isOpen={!!deletingFeed}
        feedName={deletingFeed?.name || ''}
        onClose={() => setDeletingFeed(null)}
        onConfirm={confirmDeleteFeed}
        isDeleting={deleteFeedMutation.isPending}
      />
    </div>
  )
}